// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import dynamicProto from "@microsoft/dynamicproto-js";
import { BreezeChannelIdentifier, SenderPostManager, _throwInternal, createProcessTelemetryContext, formatErrorMessageXdr, getResponseText, onConfigChange, parseResponse, prependTransports, utlSetStoragePrefix } from "@microsoft/applicationinsights-core-js";
import { isFunction } from "@nevware21/ts-utils";
import { _DYN_INITIALIZE, _DYN_IS_COMPLETELY_IDLE } from "./__DynamicConstants";
var DefaultOfflineIdentifier = "OfflineChannel";
var PostChannelId = "PostChannel";
function isOverrideFn(httpXHROverride) {
    return httpXHROverride && httpXHROverride.sendPOST;
}
var Sender = /** @class */ (function () {
    function Sender() {
        var _consecutiveErrors; // How many times in a row a retryable error condition has occurred.
        var _retryAt; // The time to retry at in milliseconds from 1970/01/01 (this makes the timer calculation easy).
        var _paused; // Flag indicating that the sending should be paused
        var _enableSendPromise;
        var _alwaysUseCustomSend;
        var _isInitialized;
        var _diagLog;
        var _core;
        var _httpInterface;
        var _onlineChannelId;
        var _isOneDs;
        var _sendPostMgr;
        var _disableCredentials;
        var _fetchCredentials;
        dynamicProto(Sender, this, function (_self, _base) {
            //let _sendCredentials = true; // for 1ds
            _initDefaults();
            _self.pause = function () {
                _clearScheduledTimer();
                _paused = true;
            };
            _self.resume = function () {
                if (_paused) {
                    _paused = false;
                    _retryAt = null;
                }
            };
            _self.getXhrInst = function (sync) {
                // unload events will be saved. so not return unload interface
                return _httpInterface;
            };
            _self[_DYN_INITIALIZE /* @min:%2einitialize */] = function (config, core, cxt, diagLog, channelId, unloadHookContainer) {
                _diagLog = diagLog || core.logger;
                if (_isInitialized) {
                    _throwInternal(_diagLog, 1 /* eLoggingSeverity.CRITICAL */, 28 /* _eInternalMessageId.SenderNotInitialized */, "Sender is already initialized");
                }
                _core = core;
                _consecutiveErrors = 0;
                _retryAt = null;
                // This function will be re-called whenever any referenced configuration is changed
                var hook = onConfigChange(config, function (details) {
                    var config = details.cfg;
                    if (config.storagePrefix) {
                        utlSetStoragePrefix(config.storagePrefix);
                    }
                    var ctx = createProcessTelemetryContext(null, config, core);
                    var offlineCfg = ctx.getExtCfg(DefaultOfflineIdentifier);
                    _onlineChannelId = channelId || BreezeChannelIdentifier;
                    var senderConfig = ctx.getExtCfg(_onlineChannelId, {});
                    var offlineSenderCfg = offlineCfg.senderCfg || {};
                    _fetchCredentials = null;
                    if (_onlineChannelId == PostChannelId) {
                        _isOneDs = true;
                        var channelConfig = ctx.getExtCfg(PostChannelId);
                        if (channelConfig && channelConfig["fetchCredentials"]) {
                            _fetchCredentials = channelConfig["fetchCredentials"];
                        }
                    }
                    _alwaysUseCustomSend = offlineSenderCfg.alwaysUseXhrOverride;
                    // default true
                    _enableSendPromise = !(senderConfig.enableSendPromise === false);
                    var xhrOverride = offlineSenderCfg.httpXHROverride || senderConfig.httpXHROverride;
                    var customInterface = isOverrideFn(xhrOverride) ? xhrOverride : null;
                    _disableCredentials = !customInterface && _isOneDs;
                    var sendPostMgrConfig = _getSendPostMgrConfig();
                    if (!_sendPostMgr) {
                        _sendPostMgr = new SenderPostManager();
                        _sendPostMgr[_DYN_INITIALIZE /* @min:%2einitialize */](sendPostMgrConfig, _diagLog);
                    }
                    else {
                        _sendPostMgr.SetConfig(sendPostMgrConfig);
                    }
                    var httpInterface = null;
                    var customTransPorts = offlineSenderCfg.transports || senderConfig.transports || [];
                    var theTransports = prependTransports([1 /* TransportType.Xhr */, 2 /* TransportType.Fetch */, 3 /* TransportType.Beacon */], customTransPorts);
                    httpInterface = _sendPostMgr.getSenderInst(theTransports, false);
                    var xhrInterface = _sendPostMgr.getFallbackInst();
                    httpInterface = _alwaysUseCustomSend ? customInterface : (httpInterface || customInterface || xhrInterface);
                    _httpInterface = httpInterface || xhrInterface;
                });
                unloadHookContainer && unloadHookContainer.add(hook);
            };
            _self[_DYN_IS_COMPLETELY_IDLE /* @min:%2eisCompletelyIdle */] = function () {
                var syncPayload = 0;
                try {
                    var senderPlugin = _core.getPlugin(_onlineChannelId).plugin;
                    if (senderPlugin && isFunction(senderPlugin[_DYN_IS_COMPLETELY_IDLE /* @min:%2eisCompletelyIdle */])) {
                        if (!senderPlugin[_DYN_IS_COMPLETELY_IDLE /* @min:%2eisCompletelyIdle */]()) {
                            return false;
                        }
                    }
                    if (_sendPostMgr) {
                        syncPayload = _sendPostMgr.getSyncFetchPayload();
                    }
                }
                catch (e) {
                    // if can't get idle status of online sender, then isidle status only depends on offine sender idle status
                }
                return !_paused && syncPayload === 0;
            };
            // partial 206, parse payload (not send again)
            _self._doTeardown = function (unloadCtx, unloadState) {
                _initDefaults();
            };
            /**
             * success handler
             */
            function _onSuccess(res, onComplete) {
                _doOnComplete(onComplete, 200, {}, res);
            }
            function _getSendPostMgrConfig() {
                var config = {
                    enableSendPromise: _enableSendPromise,
                    isOneDs: _isOneDs,
                    disableCredentials: _disableCredentials,
                    fetchCredentials: _fetchCredentials,
                    senderOnCompleteCallBack: _getOnCompleteFuncs()
                };
                return config;
            }
            /**
             * error handler
             */
            function _onError(message, onComplete) {
                _throwInternal(_diagLog, 2 /* eLoggingSeverity.WARNING */, 26 /* _eInternalMessageId.OnError */, "Failed to send telemetry.", { message: message });
                _doOnComplete(onComplete, 400, {});
            }
            function _getOnCompleteFuncs() {
                var funcs = {
                    xdrOnComplete: function (response, oncomplete) {
                        return _xdrOnLoad(response, oncomplete);
                    },
                    fetchOnComplete: function (response, onComplete, resValue) {
                        var status = response.status;
                        return _handleResponse(onComplete, status, {}, resValue);
                    },
                    xhrOnComplete: function (request, oncomplete) {
                        var response = getResponseText(request);
                        return _handleResponse(oncomplete, request.status, {}, response);
                    }
                };
                return funcs;
            }
            function _doOnComplete(oncomplete, status, headers, response) {
                try {
                    oncomplete && oncomplete(status, headers, response);
                }
                catch (e) {
                    // eslint-disable-next-line no-empty
                }
            }
            function _handleResponse(oncomplete, status, headers, response) {
                if (status == 206 && !_isOneDs) {
                    // for breeze, 206 is partially success, currently consider success
                    // TODO: handle partial success
                    _doOnComplete(oncomplete, 200, headers, response); // TODO: doc (support partial success)-> partial success add known issue (breeze)
                }
                else if (status == 204 && _isOneDs) {
                    // one collector
                    _doOnComplete(oncomplete, 200, headers, response);
                }
                else {
                    _doOnComplete(oncomplete, status, headers, response);
                }
            }
            function _clearScheduledTimer() {
                _retryAt = null;
            }
            /**
             * xdr state changes
             */
            function _xdrOnLoad(xdr, oncomplete) {
                var responseText = getResponseText(xdr);
                if (xdr && (responseText + "" === "200" || responseText === "")) {
                    _consecutiveErrors = 0;
                    _onSuccess(responseText, oncomplete);
                }
                else {
                    var results = parseResponse(responseText, _diagLog);
                    if (results && results.itemsAccepted) {
                        // TODO: onPartial success for appInsights
                        _onSuccess(responseText, oncomplete);
                    }
                    else {
                        _onError(formatErrorMessageXdr(xdr), oncomplete);
                    }
                }
            }
            function _initDefaults() {
                _self._appId = null;
                _consecutiveErrors = 0;
                _retryAt = null;
                _paused = false;
                _isInitialized = false;
                _core = null;
                _onlineChannelId = null;
                _sendPostMgr = null;
            }
        });
    }
    /**
     * Pause the sending (transmission) of events, this will cause all events to be batched only until the maximum limits are
     * hit at which point new events are dropped. Will also cause events to NOT be sent during page unload, so if Session storage
     * is disabled events will be lost.
     */
    Sender.prototype.pause = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Resume the sending (transmission) of events, this will restart the timer and any batched events will be sent using the normal
     * send interval.
     */
    Sender.prototype.resume = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    Sender.prototype.initialize = function (config, core, cxt, diagLog, channelId, unloadHookContainer) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Trigger the immediate send of buffered data; If executing asynchronously (the default) this may (not required) return
     * an [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html) that will resolve once the
     * send is complete. The actual implementation of the `IPromise` will be a native Promise (if supported) or the default
     * as supplied by [ts-async library](https://github.com/nevware21/ts-async)
     * @param async - Indicates if the events should be sent asynchronously
     * @param forcedSender - Indicates the forcedSender, undefined if not passed
     * @returns - Nothing or optionally, if occurring asynchronously a [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html)
     * which will be resolved (or reject) once the send is complete, the [IPromise](https://nevware21.github.io/ts-async/typedoc/interfaces/IPromise.html)
     * should only be returned when async is true.
     */
    Sender.prototype.triggerSend = function (async, forcedSender, sendReason) {
        if (async === void 0) { async = true; }
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Check if there are no active requests being sent.
     * @returns True if idle, false otherwise.
     */
    Sender.prototype.isCompletelyIdle = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return false;
    };
    /**
     * Get current xhr instance
     */
    Sender.prototype.getXhrInst = function (sync) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    Sender.prototype._doTeardown = function (unloadCtx, unloadState) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    return Sender;
}());
export { Sender };
//# sourceMappingURL=Sender.js.map